import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { del, get, post } from '../lib/api';
import IconPicker from '../components/IconPicker';
import { Avatar, ErrorNote, Sheet, Spinner } from '../components/ui';
import type { Child, Profile, TurnJob } from '../lib/types';

const EMOJIS = ['🧒', '👦', '👧', '🧑', '👩', '👨', '🦊', '🐼', '🦄', '⚽️'];

export default function ParentFamily(): JSX.Element {
  const [data, setData] = useState<{ parents: Profile[]; children: Child[] } | null>(null);
  const [adding, setAdding] = useState<'child' | 'parent' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    get<{ parents: Profile[]; children: Child[] }>('/api/users')
      .then(setData)
      .catch((err) => setError((err as Error).message));
  }, []);

  useEffect(load, [load]);

  async function deactivate(id: number, name: string) {
    if (!confirm(`Remove ${name}? Their history is kept, they just cannot sign in any more.`)) return;
    await del(`/api/users/${id}`);
    load();
  }

  if (!data) return <Spinner />;

  return (
    <>
      <div className="appbar">
        <Link className="btn ghost" to="/">
          ‹ Family
        </Link>
      </div>
      <h1>Manage family</h1>
      <ErrorNote error={error} />

      <h2>Kids</h2>
      <div className="stack">
        {data.children.map((child) => (
          <div key={child.id} className="card tight row between">
            <Link
              to={`/child/${child.id}`}
              className="row grow"
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              <Avatar emoji={child.emoji} />
              <span className="grow">
                <strong>{child.name}</strong>
                <span className="tiny muted" style={{ display: 'block' }}>
                  {child.active ? 'Active' : 'Removed'}
                </span>
              </span>
            </Link>
            {child.active === 1 && (
              <button className="btn ghost" type="button" onClick={() => void deactivate(child.id, child.name)}>
                Remove
              </button>
            )}
          </div>
        ))}
      </div>
      <button className="btn block secondary" type="button" style={{ marginTop: 10 }} onClick={() => setAdding('child')}>
        + Add a child
      </button>
      <Link className="btn block secondary" to="/family/starter" style={{ marginTop: 10 }}>
        📋 Load our chore list
      </Link>

      <h2>Parents</h2>
      <div className="stack">
        {data.parents.map((parent) => (
          <div key={parent.id} className="card tight row between">
            <div className="row grow">
              <Avatar emoji={parent.emoji} />
              <strong>{parent.name}</strong>
            </div>
          </div>
        ))}
      </div>
      <button className="btn block secondary" type="button" style={{ marginTop: 10 }} onClick={() => setAdding('parent')}>
        + Add a parent
      </button>

      <h2>Shared jobs</h2>
      <p className="tiny muted" style={{ marginTop: -6 }}>
        Jobs the kids take turns at instead of ones that fall due on a day. Whoever has done fewest is
        up next; doing one out of turn just puts them ahead.
      </p>
      <TurnJobs children={data.children.filter((child) => child.active === 1)} />

      {adding && (
        <AddPerson
          role={adding}
          onClose={() => setAdding(null)}
          onSaved={() => {
            setAdding(null);
            load();
          }}
        />
      )}
    </>
  );
}

function TurnJobs({ children }: { children: Child[] }): JSX.Element {
  const [jobs, setJobs] = useState<TurnJob[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState('');
  const [icon, setIcon] = useState('🍽️');
  const [points, setPoints] = useState(1);
  const [picked, setPicked] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    get<{ jobs: TurnJob[] }>('/api/turn-jobs')
      .then((res) => setJobs(res.jobs))
      .catch((err) => setError((err as Error).message));
  }, []);
  useEffect(load, [load]);
  useEffect(() => setPicked(children.map((child) => child.id)), [children]);

  async function create() {
    setError(null);
    if (!title.trim()) {
      setError('Give the job a name.');
      return;
    }
    try {
      await post('/api/turn-jobs', { title: title.trim(), icon, points, childIds: picked });
      setTitle('');
      setAdding(false);
      load();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  async function remove(job: TurnJob) {
    if (!confirm(`Remove "${job.title}"? The turns taken so far go with it.`)) return;
    await del(`/api/turn-jobs/${job.id}`);
    load();
  }

  if (!jobs) return <Spinner />;

  return (
    <>
      <ErrorNote error={error} />
      <div className="stack">
        {jobs.map((job) => (
          <div key={job.id} className="card tight">
            <div className="row between">
              <div className="row grow">
                <span style={{ fontSize: 22 }}>{job.icon}</span>
                <span className="grow">
                  <strong>{job.title}</strong>
                  <span className="tiny muted" style={{ display: 'block' }}>
                    {job.nextName}'s turn · {job.points} {job.points === 1 ? 'point' : 'points'} each
                  </span>
                </span>
              </div>
              <button className="btn ghost" type="button" onClick={() => void remove(job)}>
                Remove
              </button>
            </div>
            <div className="chips" style={{ marginTop: 8 }}>
              {job.standings.map((standing) => (
                <span key={standing.childId} className={`chip${standing.ahead > 0 ? ' on' : ''}`}>
                  {standing.emoji} {standing.name}: {standing.turns}
                  {standing.ahead > 0 ? ` (+${standing.ahead})` : ''}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>

      {adding ? (
        <div className="card" style={{ marginTop: 10 }}>
          <label className="field">
            <span>Job</span>
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Empty the dishwasher" />
          </label>
          <IconPicker value={icon} onChange={setIcon} />
          <label className="field">
            <span>Points per turn</span>
            <input
              type="number"
              min={1}
              max={20}
              value={points}
              onChange={(e) => setPoints(Number(e.target.value))}
            />
          </label>
          <div className="chips" style={{ marginBottom: 10 }}>
            {children.map((child) => (
              <button
                key={child.id}
                type="button"
                className={`chip${picked.includes(child.id) ? ' on' : ''}`}
                onClick={() =>
                  setPicked((current) =>
                    current.includes(child.id)
                      ? current.filter((id) => id !== child.id)
                      : [...current, child.id],
                  )
                }
              >
                {child.emoji} {child.name}
              </button>
            ))}
          </div>
          <div className="row">
            <button className="btn grow" type="button" onClick={() => void create()}>
              Add job
            </button>
            <button className="btn ghost" type="button" onClick={() => setAdding(false)}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button className="btn block secondary" type="button" style={{ marginTop: 10 }} onClick={() => setAdding(true)}>
          + Add a shared job
        </button>
      )}
    </>
  );
}

function AddPerson({
  role,
  onClose,
  onSaved,
}: {
  role: 'child' | 'parent';
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const [name, setName] = useState('');
  const [emoji, setEmoji] = useState(role === 'child' ? '🧒' : '🧑');
  const [pin, setPin] = useState('');
  const [weekly, setWeekly] = useState(0);
  const [monthly, setMonthly] = useState(0);
  const [threshold, setThreshold] = useState(80);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function save() {
    setError(null);
    if (!name.trim()) {
      setError('Give them a name.');
      return;
    }
    if (!/^\d{4,8}$/.test(pin)) {
      setError('Pick a PIN of 4-8 digits.');
      return;
    }
    setBusy(true);
    try {
      await post('/api/users', {
        name: name.trim(),
        role,
        pin,
        emoji,
        ...(role === 'child'
          ? {
              allowance_weekly: weekly,
              allowance_monthly: monthly,
              qualify_threshold: threshold / 100,
            }
          : {}),
      });
      onSaved();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet title={role === 'child' ? 'Add a child' : 'Add a parent'} onClose={onClose}>
      <ErrorNote error={error} />
      <label className="field">
        <span>Name</span>
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={40} />
      </label>
      <div className="field">
        <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--muted)', marginBottom: 6 }}>
          Avatar
        </span>
        <div className="chips">
          {EMOJIS.map((option) => (
            <button
              key={option}
              type="button"
              className={`chip${emoji === option ? ' on' : ''}`}
              style={{ fontSize: 20 }}
              onClick={() => setEmoji(option)}
            >
              {option}
            </button>
          ))}
        </div>
      </div>
      <label className="field">
        <span>PIN (4-8 digits)</span>
        <input
          className="pin-input"
          inputMode="numeric"
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 8))}
        />
      </label>

      {role === 'child' && (
        <>
          <div className="row">
            <label className="field grow">
              <span>Weekly allowance</span>
              <input type="number" min={0} value={weekly} onChange={(e) => setWeekly(Number(e.target.value))} />
            </label>
            <label className="field grow">
              <span>Monthly allowance</span>
              <input type="number" min={0} value={monthly} onChange={(e) => setMonthly(Number(e.target.value))} />
            </label>
          </div>
          <label className="field">
            <span>Chores needed to qualify (%)</span>
            <input
              type="number"
              min={0}
              max={100}
              value={threshold}
              onChange={(e) => setThreshold(Number(e.target.value))}
            />
          </label>
        </>
      )}

      <button className="btn block" type="button" onClick={save} disabled={busy}>
        {busy ? 'Saving…' : 'Add'}
      </button>
    </Sheet>
  );
}
